import { useState } from "react";
import { motion } from "framer-motion";
import { Send, MapPin, Phone, Mail, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";

const Contact = () => {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const trimmedName = form.name.trim();
    const trimmedEmail = form.email.trim();
    const trimmedMessage = form.message.trim();

    if (!trimmedName || !trimmedEmail || !trimmedMessage) {
      toast.error("Todos los campos son obligatorios.");
      return;
    }
    if (trimmedName.length > 200) {
      toast.error("El nombre es demasiado largo (máx. 200 caracteres).");
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedEmail) || trimmedEmail.length > 255) {
      toast.error("Por favor ingresa un email válido.");
      return;
    }
    if (trimmedMessage.length > 5000) {
      toast.error("El mensaje es demasiado largo (máx. 5000 caracteres).");
      return;
    }

    setLoading(true);

    try {
      const id = crypto.randomUUID();

      // Save submission (RLS denies anon inserts, so route through edge function or use insert via service later if needed)
      // We use a single edge function invocation pattern: insert via DB requires service key,
      // so we use the transactional email function for both confirmation and admin notification.
      // Persist in DB through a lightweight RPC/function call by reusing send-transactional-email metadata.

      // Insert submission via authenticated path is blocked; instead, send admin email which carries the message.
      const adminPromise = supabase.functions.invoke("send-transactional-email", {
        body: {
          templateName: "contact-admin-notification",
          recipientEmail: "herberaldana@integradocgt.com",
          idempotencyKey: `contact-admin-${id}`,
          templateData: { name: trimmedName, email: trimmedEmail, message: trimmedMessage, submissionId: id },
        },
      });

      const userPromise = supabase.functions.invoke("send-transactional-email", {
        body: {
          templateName: "contact-confirmation",
          recipientEmail: trimmedEmail,
          idempotencyKey: `contact-confirm-${id}`,
          templateData: { name: trimmedName },
        },
      });

      const [adminRes] = await Promise.all([adminPromise, userPromise]);
      if (adminRes.error) throw adminRes.error;

      toast.success("¡Gracias! Te responderemos en 2–4 horas hábiles.");
      setForm({ name: "", email: "", message: "" });
    } catch (err) {
      console.error("Contact form error:", err);
      toast.error("Hubo un error al enviar tu mensaje. Intenta de nuevo.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contacto" className="py-24 bg-background">
      <div className="container">
        <div className="grid lg:grid-cols-2 gap-16">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
          >
            <span className="text-sm font-semibold tracking-wider uppercase text-primary mb-3 block">
              Contacto
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold text-foreground mb-6">
              Hablemos de tu caso
            </h2>
            <p className="text-muted-foreground text-lg mb-10 leading-relaxed">
              ¡Gracias por tu interés! Te responderemos en un plazo de 2–4 horas hábiles
              sobre conservación de DTE en Guatemala.
            </p>

            <div className="space-y-5">
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 rounded-lg bg-accent flex items-center justify-center">
                  <MapPin size={18} className="text-accent-foreground" />
                </div>
                <span className="text-foreground">Guatemala</span>
              </div>
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 rounded-lg bg-accent flex items-center justify-center">
                  <Phone size={18} className="text-accent-foreground" />
                </div>
                <span className="text-foreground">+502 3382-3427</span>
              </div>
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 rounded-lg bg-accent flex items-center justify-center">
                  <Mail size={18} className="text-accent-foreground" />
                </div>
                <span className="text-foreground">herberaldana@integradocgt.com</span>
              </div>
            </div>
          </motion.div>

          <motion.form
            onSubmit={handleSubmit}
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="glass rounded-2xl p-8 space-y-5"
          >
            <div>
              <label className="text-sm font-medium text-foreground mb-1.5 block">Nombre y apellido *</label>
              <input
                required
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="w-full rounded-lg border border-border bg-background px-4 py-3 text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                placeholder="Tu nombre"
                disabled={loading}
              />
            </div>
            <div>
              <label className="text-sm font-medium text-foreground mb-1.5 block">Email *</label>
              <input
                required
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="w-full rounded-lg border border-border bg-background px-4 py-3 text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                placeholder="tu@email.com"
                disabled={loading}
              />
            </div>
            <div>
              <label className="text-sm font-medium text-foreground mb-1.5 block">Mensaje *</label>
              <textarea
                required
                rows={4}
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                className="w-full rounded-lg border border-border bg-background px-4 py-3 text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring resize-none"
                placeholder="¿En qué podemos ayudarte?"
                disabled={loading}
              />
            </div>
            <button
              type="submit"
              disabled={loading}
              className="w-full inline-flex items-center justify-center gap-2 rounded-lg bg-primary px-7 py-3.5 text-base font-semibold text-primary-foreground hover:bg-primary/90 transition-colors disabled:opacity-60"
            >
              {loading ? (
                <>
                  <Loader2 size={18} className="animate-spin" />
                  Enviando...
                </>
              ) : (
                <>
                  Enviar <Send size={18} />
                </>
              )}
            </button>
          </motion.form>
        </div>
      </div>
    </section>
  );
};

export default Contact;
